import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import MoreActionsMenu from "@components/ListItemCardCst/MoreActionsMenu";
import { ListItemAction } from "@components/ListItemCardCst/types";
import BadgeBoxCst from "@components/BadgeBoxCst";
import IconMegaphone from "@components/CustomIcons/IconMegaphone";
import { EnrolledGroupItem } from "../types";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

interface EnrolledGroupRowProps {
  group: EnrolledGroupItem;
  onGoToGroup?: (group: EnrolledGroupItem) => void;
  onEditEnrollment?: (group: EnrolledGroupItem) => void;
  onDeleteEnrollment?: (group: EnrolledGroupItem) => void;
  onSolicit?: (group: EnrolledGroupItem) => void;
  // S05: azioni previste ma non consentite dai permessi, mostrate disabilitate.
  disabledActions?: { edit?: boolean; delete?: boolean; solicit?: boolean };
}

const EnrolledGroupRow: React.FC<EnrolledGroupRowProps> = ({
  group,
  onGoToGroup,
  onEditEnrollment,
  onDeleteEnrollment,
  onSolicit,
  disabledActions = {},
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const typeLabel = group.groupType === "DYNAMIC" ? "Dinamico" : "Statico";
  const enrollLabel = formatEnrollTypeLabel(group.enrollmentType);

  const enrollmentActions: ListItemAction[] =
    onEditEnrollment || onDeleteEnrollment
      ? [
          {
            label: String(t("card.actions.edit-group-enrollment")),
            icon: <Pencil size={14} />,
            onClick: () => onEditEnrollment?.(group),
            disabled:
              !onEditEnrollment || !group.groupType || !!disabledActions.edit,
          },
          {
            label: String(t("card.actions.delete-group-enrollment")),
            icon: <Trash2 size={14} />,
            onClick: () => onDeleteEnrollment?.(group),
            disabled:
              !onDeleteEnrollment ||
              !group.groupType ||
              !!disabledActions.delete,
          },
        ]
      : [];

  const moreActions: ListItemAction[] = [
    ...enrollmentActions,
    ...(onSolicit
      ? [
          {
            label: String(t("card.actions.solicit")),
            icon: <IconMegaphone size={14} />,
            onClick: () => onSolicit(group),
            disabled: !group.groupType || !!disabledActions.solicit,
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "14px 16px",
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {group.groupName || `Gruppo ${group.id}`}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.primary,
            lineHeight: 1.4,
          }}
        >
          {`ID: ${group.id} | ${typeLabel}`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            justifyContent: "flex-start",
            flexWrap: "wrap",
          },
        }}
      >
        {enrollLabel && <BadgeBoxCst>{enrollLabel}</BadgeBoxCst>}

        {onGoToGroup && (
          <ButtonSecondary size="sm" onClick={() => onGoToGroup(group)}>
            {t("card.details")}
          </ButtonSecondary>
        )}

        <MoreActionsMenu actions={moreActions} />
      </Box>
    </Box>
  );
};

export default EnrolledGroupRow;
